import { useState, useEffect, useRef } from "react";

export const useRequestTracker = () => {
  const [requestController, setRequestController] = useState(
    new AbortController()
  );
  const requestCount = useRef(0);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
      requestController.abort();
    };
  }, []);

  const createNewRequestController = () => {
    if (isMounted.current) {
      requestController.abort();
      const newController = new AbortController();
      setRequestController(newController);
      requestCount.current += 1;
      return { controller: newController, requestCount: requestCount.current };
    }
    return {
      controller: new AbortController(),
      requestCount: requestCount.current,
    };
  };

  const trackRequest = async ({
    executeRequest,
    onSuccess,
    onError,
    onFinally,
  }) => {
    const { controller, requestCount: currentRequestCount } =
      createNewRequestController();

    try {
      const result = await executeRequest(controller.signal);
      if (requestCount.current === currentRequestCount) {
        onSuccess?.(result);
      }
    } catch (error) {
      if (requestCount.current === currentRequestCount) {
        onError?.(error);
      }
    } finally {
      if (requestCount.current === currentRequestCount) {
        onFinally?.();
      }
    }
  };

  return { trackRequest, createNewRequestController, requestController };
}
